import { shortSpinId } from '../../../Model/Spin/spinId.js'
import React, { useMemo, useState } from 'react'
import { Link, Navigate } from 'react-router-dom'
import { useGetAuthenticatedUser } from '../../../Flux/Selector/index.js'
import { isGlobalAdmin } from '../../../Model/User/IUser.js'
import { StatsBarChart } from '../AdminStatsPage/StatsBarChart.js'
import { Card } from '../AdminStatsPage/StatsUi.js'
import { SpinChrome } from './SpinChrome.js'
import { InfoTip } from './SpinUi.js'
import {
  fmt,
  histogramToData,
  perSourceAverages,
  reachHistogram,
  sizeHistogram,
  sourcesOf,
  tsLabel,
  unityHistogram,
  worstUnity
} from './spinMetrics.logic.js'
import { useSelectedRun } from './useSelectedRun.js'
import { useSpinCohesion, useSpinMetrics } from './useSpinData.js'

const TABLE_LIMIT = 100

export const SpinCohesionPage = () => {
  const user = useGetAuthenticatedUser()
  const sel = useSelectedRun()
  const { selected } = sel
  const { metrics, loading: metricsLoading, error: metricsError } = useSpinMetrics(selected)
  const { cohesion, loading: fullLoading, error: fullError, load: loadFull } = useSpinCohesion(selected)

  const spins = metrics?.spins ?? []
  const sources = useMemo(() => sourcesOf(spins), [spins])
  const [unitySource, setUnitySource] = useState('Consensus')
  const effectiveSource = sources.includes(unitySource) ? unitySource : (sources[0] ?? '')

  const sizeData = useMemo(() => sizeHistogram(metrics?.sizeDistribution ?? []), [metrics])
  const unityData = useMemo(() => unityHistogram(spins, effectiveSource), [spins, effectiveSource])
  const reachData = useMemo(() => reachHistogram(spins, effectiveSource), [spins, effectiveSource])
  const worstSpins = useMemo(() => [...spins].sort((a, b) => worstUnity(a) - worstUnity(b)).slice(0, TABLE_LIMIT), [spins])
  const sourceAverages = useMemo(() => perSourceAverages(spins, sources), [spins, sources])

  // Full-set (opt-in) cohesion, for the currently-selected distribution source.
  const fullSource = useMemo(() => cohesion?.perSource?.find((s) => s.source === effectiveSource), [cohesion, effectiveSource])
  const fullUnityData = useMemo(() => histogramToData(fullSource?.unityHistogram ?? []), [fullSource])
  const fullReachData = useMemo(() => histogramToData(fullSource?.reachHistogram ?? []), [fullSource])

  if (!user) return <></>
  if (!isGlobalAdmin(user)) return <Navigate to="/home" replace />

  const shown = spins.length

  return (
    <div className="space-y-8">
      <SpinChrome title="SPIN · Cohesion" sel={sel} />

      {metricsLoading && <p className="text-gray-500">Loading metrics…</p>}
      {metricsError && <p className="text-error">Could not load metrics: {metricsError}</p>}

      {!metricsLoading && !metricsError && metrics?.snapshot && (
        <>
          <h2 className="text-xl font-semibold">
            Current set — {metrics.snapshot.amplicon ?? '—'} as of {tsLabel(metrics.snapshot.asOf)}
            <InfoTip tip="The live SPIN set for this algorithm+amplicon as of the selected run's timestamp — latest assignment per OTU (currency by assigned_at, not run id). A run is always for a single amplicon." />
          </h2>

          <p className="text-sm text-gray-500">
            Unity, reach and the most-mixed table below are computed over the <b>largest {shown.toLocaleString()} SPINs</b> (the detail
            set the API returns, biggest-first) — not the whole set. The Size distribution and the dashboard KPIs are whole-set. A full-set
            cohesion view is coming as an explicit, slower load.
          </p>

          <Card title={`Per-source consensus — largest ${shown.toLocaleString()} SPINs`}>
            {sourceAverages.length === 0 ? (
              <p className="text-gray-500 text-sm">No taxonomy resolved in this set.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="table table-xs">
                  <thead>
                    <tr>
                      <th>Source</th>
                      <th className="text-right">
                        Avg unity
                        <InfoTip tip="Agreement: mean over the 8 taxonomy ranks of the per-rank modal share (do a SPIN's members resolve to the same taxon?). 1 = fully coherent." />
                      </th>
                      <th className="text-right">
                        Avg reach
                        <InfoTip tip="Depth: mean over members of filled ranks ÷ per-source denominator (null/empty/nan = empty). Higher = more fully resolved. Low reach usually lifts unity (members agree on nothing)." />
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {sourceAverages.map((r) => (
                      <tr key={r.source}>
                        <td>{r.source}</td>
                        <td className="text-right">{r.avgUnity == null ? '—' : r.avgUnity.toFixed(3)}</td>
                        <td className="text-right">{r.avgReach == null ? '—' : r.avgReach.toFixed(3)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          <Card title="Size distribution (members per SPIN) — full set">
            <StatsBarChart data={sizeData} />
          </Card>

          <div className="flex items-center gap-2 text-sm">
            <span className="text-gray-500">Source</span>
            <select
              className="select select-bordered select-xs"
              value={effectiveSource}
              onChange={(e) => setUnitySource(e.target.value)}
            >
              {sources.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card title={`Unity distribution — largest ${shown.toLocaleString()} SPINs`}>
              <StatsBarChart data={unityData} color="#8b5cf6" rotateLabels />
            </Card>
            <Card title={`Reach distribution — largest ${shown.toLocaleString()} SPINs`}>
              <StatsBarChart data={reachData} color="#0ea5e9" rotateLabels />
            </Card>
          </div>

          <Card title={`Most-mixed SPINs (lowest unity first, of the largest ${shown.toLocaleString()})`}>
            <div className="overflow-x-auto max-h-[32rem]">
              <table className="table table-xs table-pin-rows">
                <thead>
                  <tr>
                    <th>SPIN</th>
                    <th>Amplicon</th>
                    <th className="text-right">Members</th>
                    {sources.map((s) => (
                      <th key={s} className="text-right">
                        {s}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {worstSpins.map((s) => (
                    <tr key={s.spinId}>
                      <td className="font-mono">
                        <Link to={`/admin/spin/s/${s.spinId}`} className="link link-primary">
                          {shortSpinId(s.spinId)}
                        </Link>
                      </td>
                      <td>{s.amplicon ?? '—'}</td>
                      <td className="text-right">{s.memberCount}</td>
                      {sources.map((src) => (
                        <td key={src} className="text-right">
                          {fmt((s.unityBySource ?? {})[src])}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}

      {selected && (
        <section className="space-y-4 border-t border-gray-200 pt-6">
          <div className="flex flex-wrap items-center gap-3">
            <h2 className="text-xl font-semibold">
              Full-set cohesion — all current SPINs
              <InfoTip tip="Unity/reach over EVERY current SPIN, not just the largest N. Heavy to compute, so it loads on demand. Singletons trivially score unity 1.0, so expect the top bin to dominate the whole-set view." />
            </h2>
            <button className="btn btn-sm btn-primary" onClick={loadFull} disabled={fullLoading}>
              {fullLoading ? 'Loading… (~1 min)' : cohesion ? 'Reload full set' : 'Load full set (~1 min)'}
            </button>
          </div>
          {fullError && <p className="text-error">Could not load full-set cohesion: {fullError}</p>}
          {cohesion && (
            <>
              <Card title="Per-source consensus — all current SPINs">
                <div className="overflow-x-auto">
                  <table className="table table-xs">
                    <thead>
                      <tr>
                        <th>Source</th>
                        <th className="text-right">SPINs</th>
                        <th className="text-right">Avg unity</th>
                        <th className="text-right">Avg reach</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(cohesion.perSource ?? []).map((s) => (
                        <tr key={s.source}>
                          <td>{s.source}</td>
                          <td className="text-right">{s.spinCount.toLocaleString()}</td>
                          <td className="text-right">{s.avgUnity == null ? '—' : s.avgUnity.toFixed(3)}</td>
                          <td className="text-right">{s.avgReach == null ? '—' : s.avgReach.toFixed(3)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <Card title={`Unity distribution — all current SPINs (${effectiveSource || '—'})`}>
                  <StatsBarChart data={fullUnityData} color="#8b5cf6" rotateLabels />
                </Card>
                <Card title={`Reach distribution — all current SPINs (${effectiveSource || '—'})`}>
                  <StatsBarChart data={fullReachData} color="#0ea5e9" rotateLabels />
                </Card>
              </div>
            </>
          )}
        </section>
      )}
    </div>
  )
}
